import React, { useEffect } from "react";

import {
  Jurisdiction,
  Paging,
  StatusValue,
} from "../../../../application/domain/proc-management";
import { ProcedureStatusTag } from "../../../atoms/proc-management/procedure-status-tag";
import { useAuthContextV2 } from "../../../../auth-state";
import { StatusFilterDropDown } from "./status-filter-dropdown";
import { ResourceFetchError } from "../../common/resource-fetch-error";
import { defaultFetchStatuses } from "../../../../application/service/proc-management/proc-list-base";
import { Pagination } from "@graffer-inc/console-design-kit";
import { GroupMetaData } from "../../../../application/domain/group/group";
import apiClient from "../../../../application/apis/tenant-management/tenant-management-client";

export const ProcsTable: React.FC<{
  procedures: {
    id: string;
    name: string;
    contentStatus: StatusValue;
    jurisdiction: Jurisdiction;
  }[];
  orgId: string;
  paging: Paging;
  selectedStatuses: StatusValue[];
  defaultStatuses: StatusValue[];
  query?: string;
  onSelectStatus: (status: StatusValue[]) => void;
  onRowClick: (props: { orgId: string; procId: string }) => void;
  onPageClick: (props: { orgId: string; page: number }) => void;
}> = ({
  procedures,
  orgId,
  paging,
  selectedStatuses,
  defaultStatuses,
  query,
  onSelectStatus,
  onRowClick,
  onPageClick,
}) => {
  const { auth } = useAuthContextV2();
  const [jurisdictionGroupMap, setJurisdictionGroupMap] = React.useState<
    Map<string, GroupMetaData>
  >(new Map());
  const fetchGroups = React.useCallback(async () => {
    const groupMap = new Map<string, GroupMetaData>();
    try {
      const { data: groups } = await apiClient.fetchGroupsMetadata(1, 500);
      groups.forEach((group) => {
        if (group.id) {
          groupMap.set(group.id, group);
        }
      });
      setJurisdictionGroupMap(groupMap);
    } catch (e) {}
  }, [orgId, setJurisdictionGroupMap]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const includesDefaultStatuses = React.useCallback(
    () => defaultFetchStatuses.every((s) => selectedStatuses.includes(s)),
    [selectedStatuses],
  );
  return (
    <div>
      <table className="table is-fullwidth">
        <thead>
          <tr>
            <th>手続きID</th>
            <th>管理グループ</th>
            <th>手続き名称</th>
            <th>
              <StatusFilterDropDown
                selectedStatuses={selectedStatuses}
                defaultStatuses={defaultStatuses}
                onSelect={onSelectStatus}
              >
                公開ステータス
              </StatusFilterDropDown>
            </th>
          </tr>
        </thead>
        <tbody>
          {procedures.map((procedure) => (
            <tr
              key={procedure.id}
              className="c-tr-selectable c-tr-has-tag"
              onClick={() => {
                if (auth.hasPermissions("proc_management.procedures.read")) {
                  onRowClick({ orgId, procId: procedure.id });
                }
              }}
            >
              <td>{procedure.id}</td>
              <td>
                {
                  jurisdictionGroupMap.get(procedure?.jurisdiction?.groupId!)
                    ?.name
                }
              </td>
              <td>{procedure.name}</td>
              <td>
                <ProcedureStatusTag status={procedure.contentStatus} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {procedures.length > 0 ? (
        <div style={{ marginTop: "16px" }}>
          <Pagination
            onPageClick={(page) => {
              onPageClick({ orgId, page });
            }}
            currentPage={paging.current}
            lastPage={paging.last}
          />
        </div>
      ) : typeof query === "string" && query.length > 0 ? (
        <ResourceFetchError
          title={`検索クエリ「${query}」にヒットする手続きはありませんでした。`}
          subtitle={
            "検索文字列を短くする、表記ゆれを確認するなどお試しください。"
          }
        />
      ) : includesDefaultStatuses() ? (
        <ResourceFetchError
          title={`手続きがまだ作成されていません。`}
          subtitle={"「手続き新規作成」より、手続きを作成してください。"}
        />
      ) : (
        <ResourceFetchError
          title={`指定されたステータスの手続きはありませんでした。`}
          subtitle={"ステータスによる絞り込みを変更するなどお試しください。"}
        />
      )}
    </div>
  );
};

export default ProcsTable;
